/**
 * VoiceFAB — Floating Action Button for voice expense capture.
 * Always visible in the bottom-right corner. Tap to record, tap to stop;
 * it transcribes and creates the expense. Designed for hands-free use.
 *
 * 2026-06-18: keeps audio in OUR AWS account — NO browser SpeechRecognition
 * (that sent audio to Google + isn't disclosed; memory feedback_voice_capture_privacy).
 * 2026-06-19 (faster voice): the happy path is now LIVE AWS Transcribe STREAMING
 * (browser-direct, ~1-2s, partials as you speak). If streaming is unavailable
 * (Firefox/iOS quirks, no Identity Pool, cred/stream error) it falls back to the
 * batch path (MediaRecorder → presigned-PUT S3 → AWS Transcribe → poll). Both
 * paths end at the same parse → createExpense logic. Shares the hook with the
 * Capture Voice tab so the two can't drift.
 */

import { useState, useRef } from 'react';
import { Mic, X, Check, Loader2 } from 'lucide-react';
import { useExpenseCapture, VoiceBlockedError, type VoiceStreamSession } from '@/hooks/useExpenseCapture';
import { useAppStore } from '@/stores/useAppStore';
import { useToast } from '@/components/ui/Toast';
import { mapVoiceCategoryToOption } from '@/lib/voice-capture';
import { todayLocal } from '@/lib/dates';

type State = 'idle' | 'recording' | 'transcribing' | 'success' | 'error';

type ParsedVoice = {
  success: boolean;
  expense?: { vendor?: unknown; amount?: unknown; category?: unknown; businessPurpose?: unknown };
  transcript?: string;
  error?: string;
};

function pickAudioMime(): { mimeType: string; format: string } {
  if (typeof MediaRecorder === 'undefined') return { mimeType: '', format: 'webm' };
  if (MediaRecorder.isTypeSupported('audio/webm')) return { mimeType: 'audio/webm', format: 'webm' };
  if (MediaRecorder.isTypeSupported('audio/mp4')) return { mimeType: 'audio/mp4', format: 'mp4' };
  if (MediaRecorder.isTypeSupported('audio/ogg')) return { mimeType: 'audio/ogg', format: 'ogg' };
  return { mimeType: '', format: 'webm' };
}

export function VoiceFAB() {
  const [state, setState] = useState<State>('idle');
  const [transcript, setTranscript] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const { uploadAndTranscribeVoice, submitManual, startVoiceStream } = useExpenseCapture();
  const { activeEntity } = useAppStore();
  const { toast } = useToast();

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeRef = useRef<{ mimeType: string; format: string }>({ mimeType: 'audio/webm', format: 'webm' });
  const sessionRef = useRef<VoiceStreamSession | null>(null);

  const resetSoon = (ms = 3500) => setTimeout(() => { setState('idle'); setTranscript(''); setErrorMsg(''); }, ms);

  // Shared by both the streaming + batch paths: turn a parsed voice result into a
  // created expense (or an error toast).
  const finishParsed = async (parsed: ParsedVoice) => {
    if (!parsed.success || !parsed.expense) {
      setErrorMsg(parsed.error || 'Could not understand that');
      setState('error');
      resetSoon();
      return;
    }
    const exp = parsed.expense;
    const vendor = exp.vendor ? String(exp.vendor) : '';
    const amount = Number(exp.amount) || 0;
    if (!vendor || amount <= 0) {
      setErrorMsg('Couldn’t catch the vendor + amount — try again');
      setState('error');
      resetSoon();
      return;
    }
    setTranscript(parsed.transcript || '');
    const category = mapVoiceCategoryToOption(String(exp.category ?? ''))
      || (typeof exp.category === 'string' && exp.category ? exp.category : 'Uncategorized');
    const purpose = (typeof exp.businessPurpose === 'string' && exp.businessPurpose) || parsed.transcript || '';
    const res = await submitManual({ vendor, amount, expenseDate: todayLocal(), category, businessPurpose: purpose });
    if (res.success) {
      setState('success');
      toast('success', `Saved: ${vendor} $${amount.toFixed(2)}`);
      resetSoon();
    } else {
      setErrorMsg(res.error || 'Could not save the expense');
      setState('error');
      resetSoon();
    }
  };

  // Batch fallback: MediaRecorder → presigned PUT → AWS Transcribe → poll.
  const startBatchRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErrorMsg('Recording not supported here');
      setState('error');
      resetSoon();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickAudioMime();
      mimeRef.current = mime;
      const recorder = mime.mimeType ? new MediaRecorder(stream, { mimeType: mime.mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const { format } = mimeRef.current;
        const blob = new Blob(chunksRef.current, { type: `audio/${format}` });
        if (blob.size === 0) { setState('idle'); return; }
        setState('transcribing');
        try {
          const parsed = await uploadAndTranscribeVoice(blob, format);
          await finishParsed(parsed);
        } catch {
          setErrorMsg('Could not process the recording');
          setState('error');
          resetSoon();
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setState('recording');
    } catch {
      setErrorMsg('Microphone access denied');
      setState('error');
      resetSoon();
    }
  };

  const startRecording = async () => {
    if (!activeEntity) return;
    setErrorMsg('');
    setTranscript('');
    // Try live streaming first; fall back to batch on any non-cap error.
    try {
      const session = await startVoiceStream(
        (partial) => setTranscript(partial),
        () => { void stopRecording(); }, // max-session guard → finalize
      );
      sessionRef.current = session;
      setState('recording');
    } catch (e) {
      if (e instanceof VoiceBlockedError) {
        setErrorMsg(e.message);
        setState('error');
        resetSoon(5000);
        return;
      }
      await startBatchRecording();
    }
  };

  const stopRecording = async () => {
    // Streaming session active → finish it (final transcript already in hand).
    if (sessionRef.current) {
      const session = sessionRef.current;
      sessionRef.current = null;
      setState('transcribing');
      try {
        const parsed = await session.stop();
        await finishParsed(parsed);
      } catch {
        setErrorMsg('Could not process the recording');
        setState('error');
        resetSoon();
      }
      return;
    }
    // Batch path.
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
      setState('transcribing');
    }
  };

  const config = {
    idle: { bg: 'bg-primary-600 hover:bg-primary-700', icon: <Mic className="w-6 h-6" />, label: '' },
    recording: { bg: 'bg-red-500 animate-pulse', icon: <Mic className="w-6 h-6" />, label: 'Recording… tap to stop' },
    transcribing: { bg: 'bg-amber-500', icon: <Loader2 className="w-6 h-6 animate-spin" />, label: 'Saving…' },
    success: { bg: 'bg-green-500', icon: <Check className="w-6 h-6" />, label: 'Saved!' },
    error: { bg: 'bg-red-600', icon: <X className="w-6 h-6" />, label: errorMsg },
  }[state];

  return (
    <>
      {/* Overlay: live transcript while recording, or a "working" hint */}
      {(state === 'recording' || state === 'transcribing') && (
        <div className="fixed bottom-[calc(7rem_+_env(safe-area-inset-bottom))] right-4 lg:right-8 z-50 max-w-xs">
          <div className="bg-slate-900/95 backdrop-blur-md rounded-[var(--radius-lg)] p-4 shadow-2xl border border-slate-700">
            {state === 'transcribing' && transcript && (
              <p className="text-[11px] uppercase tracking-wide text-primary-300 mb-1 font-medium">Heard</p>
            )}
            {transcript
              ? <p className="text-sm text-white leading-relaxed">{transcript}</p>
              : <p className="text-sm text-white leading-relaxed">
                  {state === 'recording' ? 'Say the expense — e.g. “lunch at the cafeteria, 46 dollars”' : 'Listening…'}
                </p>}
            <p className="text-xs text-slate-400 mt-2">
              {state === 'recording' ? 'Tap the mic again to stop' : 'Saving your expense…'}
            </p>
          </div>
        </div>
      )}

      {/* Status label */}
      {config.label && (
        <div className="fixed bottom-[calc(5rem_+_env(safe-area-inset-bottom))] right-4 lg:right-8 z-50">
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${state === 'success' ? 'bg-green-100 text-green-700' : state === 'error' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
            {config.label}
          </span>
        </div>
      )}

      {/* FAB */}
      <button
        onClick={state === 'recording' ? stopRecording : state === 'idle' ? startRecording : undefined}
        disabled={state === 'transcribing' || state === 'success'}
        className={`fixed bottom-[calc(5rem_+_env(safe-area-inset-bottom))] right-4 lg:bottom-6 lg:right-8 z-50 w-14 h-14 rounded-full ${config.bg} text-white shadow-lg flex items-center justify-center transition-all`}
        aria-label={state === 'recording' ? 'Stop recording' : 'Start voice capture'}
      >
        {config.icon}
      </button>
    </>
  );
}
